import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { FileText, Sparkles, Send, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface SendNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  practitionerName: string;
}

export function SendNoteModal({ isOpen, onClose, bookingId, practitionerName }: SendNoteModalProps) {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [personalNotes, setPersonalNotes] = useState<string>('');
  const [aiSummary, setAiSummary] = useState<string>('');
  const [selectedContent, setSelectedContent] = useState<'personal' | 'ai' | null>(null);
  const [customNote, setCustomNote] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);

  // Fetch client notes and AI summary
  useEffect(() => {
    if (!isOpen || !user?.id) return;

    const fetchClientData = async () => {
      setIsLoading(true);
      
      try {
        // For now, we'll use a placeholder for personal notes since the field doesn't exist yet
        // TODO: Add personal_notes field to clients table
        setPersonalNotes('My personal health notes and wellness background will be available here once the personal notes feature is fully implemented.');

        // Get AI-generated summary (placeholder for now)
        // TODO: Implement AI summary generation
        setAiSummary('AI-generated health summary and recommendations based on your profile and previous sessions. This feature will be available soon.');
        
      } catch (error) {
        console.error('Error fetching client data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load your notes. Please try again.',
          variant: 'destructive',
          duration: 6000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientData();
  }, [isOpen, user?.id, toast]);

  const handleSendNote = async () => {
    if (!selectedContent && !customNote.trim()) {
      toast({
        title: 'No content selected',
        description: 'Please select a note to send or write a custom message.',
        variant: 'destructive',
        duration: 6000,
      });
      return;
    }

    setIsSending(true);

    try {
      // Get the conversation for this booking
      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .select('id')
        .eq('booking_id', bookingId)
        .single();

      if (convError) {
        console.error('Error finding conversation:', convError);
        toast({
          title: 'Error',
          description: 'Could not find conversation for this appointment.',
          variant: 'destructive',
          duration: 6000,
        });
        return;
      }

      // Prepare message content
      let messageContent = '';
      let messageType = 'note';

      if (customNote.trim()) {
        messageContent = `📝 Client Note:\n\n${customNote.trim()}`;
      } else if (selectedContent === 'personal') {
        messageContent = `📋 Personal Health Notes:\n\n${personalNotes}`;
        messageType = 'personal_note';
      } else if (selectedContent === 'ai') {
        messageContent = `🤖 AI Health Summary:\n\n${aiSummary}`;
        messageType = 'ai_summary';
      }

      // Send the message
      const { error: messageError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_id: user?.id,
          sender_type: 'client',
          message_content: messageContent,
          message_type: messageType,
          is_read: false,
        });

      if (messageError) {
        console.error('Error sending note:', messageError);
        toast({
          title: 'Failed to send',
          description: 'Could not send your note. Please try again.',
          variant: 'destructive',
          duration: 6000,
        });
        return;
      }

      toast({
        title: 'Note sent successfully',
        description: `Your note has been sent to ${practitionerName}.`,
        duration: 6000,
      });

      // Reset form and close modal
      setSelectedContent(null);
      setCustomNote('');
      onClose();

    } catch (error) {
      console.error('Error sending note:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
        duration: 6000,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Send Note to {practitionerName}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading your notes...</span>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Personal Notes Option */}
            {personalNotes && (
              <div 
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  selectedContent === 'personal' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedContent(selectedContent === 'personal' ? null : 'personal')}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span className="font-medium">Personal Health Notes</span>
                  </div>
                  {selectedContent === 'personal' && (
                    <Badge variant="default">Selected</Badge>
                  )}
                </div>
                <p className="text-sm text-gray-600 line-clamp-3">{personalNotes}</p>
              </div>
            )}

            {/* AI Summary Option */}
            <div 
              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                selectedContent === 'ai' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedContent(selectedContent === 'ai' ? null : 'ai')}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  <span className="font-medium">AI Health Summary</span>
                  <Badge variant="secondary">Coming Soon</Badge>
                </div>
                {selectedContent === 'ai' && (
                  <Badge variant="default">Selected</Badge>
                )}
              </div>
              <p className="text-sm text-gray-600 line-clamp-3">{aiSummary}</p>
            </div>

            {/* Custom Note Option */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-4 w-4" />
                <span className="font-medium">Write Custom Note</span>
              </div>
              <Textarea
                placeholder="Write a custom note to send to your practitioner..."
                value={customNote}
                onChange={(e) => {
                  setCustomNote(e.target.value);
                  if (e.target.value.trim()) {
                    setSelectedContent(null);
                  }
                }}
                className="min-h-[100px]"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendNote}
                disabled={isSending || (!selectedContent && !customNote.trim())}
              >
                {isSending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Note
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}